// The guide is split into many Shopify sections, each wrapper carrying .mg, so every init below
// works on whatever root it is given (the document on load, one section when the theme editor
// re-renders it) and marks what it has bound so a re-run never double-binds.
// The whole file is one guarded scope: if more than one section includes this asset, the
// extra copies exit instead of binding everything twice.
(() => {
if (window.mgGuideLoaded) return;
window.mgGuideLoaded = true;

const mgOnce = (el, key) => {
  if (el.dataset[key]) return false;
  el.dataset[key] = '1';
  return true;
};
const mgOnSectionLoad = (init) => {
  document.addEventListener('shopify:section:load', (e) => init(e.target));
};

// Desktop side labels move when an accordion opens and the copy column grows: the product labels
// (FOCUS, PRESSURE…) are justified down the column so their letters re-space, and ColdBuster's
// IMMUNE SUPPORT is centred in it so it slides down. Pin each one where it sits with every panel
// closed; resizes re-measure (allowing for whatever panels are open at the time).
(() => {
  const desktop = matchMedia('(min-width: 990px)');
  const openHeight = (scope) => {
    let h = 0;
    scope.querySelectorAll('.mg-acc__item.is-open .mg-acc__panel').forEach((panel) => { h += panel.getBoundingClientRect().height; });
    return h;
  };
  const lock = (root) => {
    root.querySelectorAll('.mg-04 .mg-prod').forEach((prod) => {
      const side = prod.querySelector('.mg-prod__side');
      if (!side) return;
      side.style.removeProperty('--side-h');
      if (!desktop.matches) return;
      const h = side.getBoundingClientRect().height - openHeight(prod);
      if (h > 0) side.style.setProperty('--side-h', `${Math.round(h)}px`);
    });
    root.querySelectorAll('.mg-11--cold').forEach((cold) => {
      const side = cold.querySelector('.mg-11__side');
      if (!side) return;
      side.style.removeProperty('--side-top');
      if (!desktop.matches) return;
      // Centred, so open panels have pushed it down by half their height.
      const closedTop = side.getBoundingClientRect().top - openHeight(cold) / 2;
      side.style.setProperty('--side-top', '0px');
      side.style.setProperty('--side-top', `${Math.round(closedTop - side.getBoundingClientRect().top)}px`);
    });
  };
  const run = () => lock(document);
  // Lock straight away (the script is deferred, so the DOM is parsed), then re-measure once fonts
  // and images have settled, since both change the column height.
  run();
  document.fonts?.ready.then(run);
  if (document.readyState !== 'complete') addEventListener('load', run);
  let t;
  addEventListener('resize', () => { clearTimeout(t); t = setTimeout(run, 150); });
  mgOnSectionLoad(lock);
})();

// Product detail accordions (MG-04 product blocks, SuperTonic, MaxiMeal and ColdBuster). CSS animates; JS only flips state.
(() => {
  const init = (root) => root.querySelectorAll(':is(.mg-04, .mg-11--cold) .mg-acc__btn').forEach((btn) => {
      if (!mgOnce(btn, 'mgAcc')) return;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        btn.closest('.mg-acc__item').classList.toggle('is-open', open);
      });
    });
  init(document);
  mgOnSectionLoad(init);
})();

// Protocol download email gate (mg-protocol). The form must submit natively: Shopify's storefront
// CAPTCHA hooks the submit and adds its token, so intercepting it (fetch) gets the post rejected.
// JS only adds the loader while the page posts, and after the reload (customer_posted=true, where
// the server renders the PDF link) starts the download once per tab session.
(() => {
  const init = (root) => root.querySelectorAll('form.mgp__gate').forEach((form) => {
    if (!mgOnce(form, 'mgGate')) return;
    // Shopify binds hCaptcha to a form on its first submit and swallows that submit, so the first
    // tap would only load the CAPTCHA. Binding it as soon as the email field is shown lets the
    // first tap go through.
    const protect = () => {
      if (form.dataset.hcaptchaBound || form.dataset.cptcha) return;
      try { window.Shopify?.captcha?.protect?.(form); } catch {}
    };
    form.querySelector('.mgp__gate-box')?.addEventListener('toggle', (e) => { if (e.target.open) protect(); });
    form.addEventListener('focusin', protect);
    let stuck;
    const stopLoading = () => {
      form.classList.remove('is-loading');
      form.removeAttribute('aria-busy');
    };
    form.addEventListener('submit', () => {
      form.classList.add('is-loading');
      form.setAttribute('aria-busy', 'true');
      const status = form.querySelector('[data-gate-status]');
      if (status) status.textContent = status.dataset.loading || '';
      // Never spin forever if the CAPTCHA (or anything else) holds the post back.
      clearTimeout(stuck);
      stuck = setTimeout(stopLoading, 10000);
    });
    const link = form.querySelector('.mgp__gate-file');
    if (link && /customer_posted=true/.test(location.search)) {
      let done = false;
      try { done = sessionStorage.getItem('mgp-downloaded') === '1'; sessionStorage.setItem('mgp-downloaded', '1'); } catch {}
      if (!done) link.click();
    }
  });
  init(document);
  mgOnSectionLoad(init);
  // Back/forward cache restores the page mid-submit: drop the loader.
  addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('form.mgp__gate.is-loading').forEach((f) => {
      f.classList.remove('is-loading');
      f.removeAttribute('aria-busy');
    });
  });
})();
;
// MG-09 testimonial carousel: native scroll-snap does the swiping; the arrows step one
// slide and are disabled at either end.
(function () {
  function init(root) {
  root.querySelectorAll('.mg-09').forEach(function (section) {
    var track = section.querySelector('.mg-09__track');
    var prev = section.querySelector('.mg-09__arrow--prev');
    var next = section.querySelector('.mg-09__arrow--next');
    if (!track || !prev || !next || !mgOnce(section, 'mgCarousel')) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Cheap reads/toggles only, so it runs directly on scroll without rAF throttling.
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      // 1px tolerance: scrollLeft can land on a fractional value because --u is fractional.
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
      section.classList.toggle('is-scrollable', max > 1);
    }

    // One slide per click: the slide pitch (width + gap) covers both mobile (one slide visible)
    // and desktop (several visible).
    function go(dir) {
      var s = track.children;
      var step = s.length > 1 ? s[1].offsetLeft - s[0].offsetLeft : track.clientWidth;
      track.scrollBy({ left: dir * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });
  }
  // The editor may hand over the section wrapper itself (which can be the .mg-09 element).
  init(document);
  mgOnSectionLoad(function (el) { init(el.parentElement || el); });
})();

;
// MG-13 FAQ accordion. Panels animate via CSS (grid rows); JS only flips state.
(() => {
  const init = (root) => root.querySelectorAll('.mg-13 .mgx-faq__btn').forEach((btn) => {
      if (!mgOnce(btn, 'mgFaq')) return;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        btn.closest('.mgx-faq__item').classList.toggle('is-open', open);
      });
    });
  init(document);
  mgOnSectionLoad(init);
})();

;
// Scroll reveals for v2 (see css/zz-motion.css). Runs once per element.
// Calibrated to the live store, which barely animates: true lists and big imagery further down
// the page. Headings, copy, buttons and accordion rows stay still, as they do on bioteenhealth.com.
// The hero has no entrance (client, 2026-10-14: it should open as one flat image), and anything
// already on screen when this runs is left alone, so nothing visible can disappear and come back.
(() => {
  const root = document.documentElement;
  // Preview pages (html.mg-verify) are the 1:1 Figma comparison renders: keep them static.
  if (root.classList.contains('mg-verify')) return;
  // Theme editor: re-rendered sections would come back hidden, so no reveals there.
  if (window.Shopify && window.Shopify.designMode) return;
  if (!('IntersectionObserver' in window)) return;
  // Reduced motion keeps a short fade so content still arrives gently, with no movement.
  const reduced = !matchMedia('(prefers-reduced-motion: no-preference)').matches;

  // [selector, effect, stagger step (ms) between siblings, base delay (ms)]
  const plan = [
    // Lists, staggered like the store's product cards
    ['.mg-sign', 'rise-sm', 80],
    ['.mg-04__jar', 'rise-sm', 80],
    ['.mg-08__point', 'rise-sm', 80],
    ['.mg-09__slide', 'rise-sm', 80],
    ['.mg-11__list > li', 'rise-sm', 80],
    ['.mgx-card', 'rise-sm', 80],
    ['.mgx-badge', 'rise-sm', 80],
    // Big photography: the store's banner zoom-out
    ['.mg-sign__img', 'zoom'],
    ['.mg-08__photo', 'zoom'],
    // Product shots arrive once each
    ['.mg-prod__media', 'rise'],
    ['.mg-05__pouch', 'rise'],
    ['.mg-11__pack', 'rise'],
  ];

  // One .mg wrapper per section, so look across the whole document (only inside the guide).
  if (!document.querySelector('.mg')) return;
  const marked = new Set();

  for (const [selector, effect, step = 0, base = 0] of plan) {
    const els = [...document.querySelectorAll(selector)].filter((el) => !marked.has(el) && el.offsetParent !== null && el.closest('.mg'));
    els.forEach((el, i) => {
      // Only hide what is still below the fold. Anything on screen (or scrolled past, e.g. a
      // restored scroll position) stays as it is, so it can never vanish and re-enter.
      if (el.getBoundingClientRect().top < innerHeight) return;
      let fx = reduced ? 'fade' : effect;
      // Zoom only where the parent clips, so the 1.2x start never spills outside its frame.
      if (fx === 'zoom') {
        const p = getComputedStyle(el.parentElement);
        if (p.overflow === 'visible' && p.overflowX === 'visible' && p.clipPath === 'none') fx = 'fade';
      }
      el.dataset.mo = fx;
      const siblingIndex = step ? Math.min([...el.parentElement.children].indexOf(el), 4) : 0;
      el.style.setProperty('--mo-delay', `${base + siblingIndex * step}ms`);
      marked.add(el);
    });
  }
  root.classList.add('mo');

  const pending = new Set(marked);
  const reveal = (el) => {
    if (!pending.delete(el)) return;
    io.unobserve(el);
    el.dataset.moRun = el.dataset.mo;
    delete el.dataset.mo;
    const delay = parseFloat(el.style.getPropertyValue('--mo-delay')) || 0;
    // Hand the element back its own transitions (hover states, accordion shadows) once done.
    setTimeout(() => {
      delete el.dataset.moRun;
      el.style.removeProperty('--mo-delay');
    }, delay + 1000); // longest reveal (zoom) is 800ms
  };

  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) reveal(entry.target);
    }
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0 });

  // A jump (scrollbar drag, restored scroll position) can carry items straight past the
  // viewport without ever intersecting; anything that has passed gets revealed anyway.
  let ticking = false;
  const sweep = () => {
    ticking = false;
    for (const el of pending) if (el.getBoundingClientRect().top < innerHeight * 0.88) reveal(el);
    if (!pending.size) removeEventListener('scroll', onScroll);
  };
  const onScroll = () => {
    if (!ticking) { ticking = true; requestAnimationFrame(sweep); }
  };

  // Start state must be painted before the first reveal, or an item scrolled into view right away would skip its animation.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    marked.forEach((el) => io.observe(el));
    addEventListener('scroll', onScroll, { passive: true });
  }));
})();

// FAQ (desktop): only the first seven questions show until "See all" is pressed.
(function () {
  function init(root) {
    root.querySelectorAll('.mgx-faq__more').forEach(function (btn) {
      if (!mgOnce(btn, 'mgMore')) return;
      btn.addEventListener('click', function () {
        var list = btn.closest('.mgx-faq');
        list.classList.add('is-all');
        btn.setAttribute('aria-expanded', 'true');
        // Move focus to the first newly shown question so keyboard users land on it.
        var next = list.querySelectorAll('.mgx-faq__item')[7];
        if (next) { var q = next.querySelector('.mgx-faq__btn'); if (q) q.focus(); }
      });
    });
  }
  init(document);
  mgOnSectionLoad(function (el) { init(el.parentElement || el); });
})();
})();
